import React, {useEffect, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {useDispatch, useSelector} from "react-redux";
import {css} from "@emotion/react";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {calculateRemSize, elevation, mb, mr, p} from "@pg-design/helpers-css-strings";
import {ArrowIcon, CloseIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {InputField} from "@pg-design/inputs-module";
import {Link} from "@pg-design/link-module";
import {Loader} from "@pg-design/loader-module";
import {transformReactHookFormProps} from "@pg-design/react-hook-forms-utils";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {useScrollObserver} from "@pg-mono/hooks";
import {RequestState} from "@pg-mono/request-state";
import {rpAppLink} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../app/rp_reducer";
import {RPModal} from "../../../atoms/RPModal";
import {addFailureNotification} from "../../../notifications/utils/add_failure_notification";
import {addWarningNotification} from "../../../notifications/utils/add_warning_notification";
import {hitApplicationSuccess, ITrackedApplication} from "../../../tracking/algolytics_hits/application_hit";
import {modalStatsHit} from "../../../tracking/algolytics_hits/modal_stats_hit";
import {IGtmRegion} from "../../../tracking/google_tag_manager/calculate_gtm_dimensions";
import {ViewType} from "../../../view_type/ViewType";
import {fetchApplicationRelatedData, initializeApplicationRelatedDataAction} from "../../actions/fetch_application_related_data";
import {usePostPhoneApplicationMutation} from "../../api/post_phone_application";
import {gtmPhoneModalDetails, hitPhoneModalShow} from "../../tracking/phone_modal_gtm";
import {IPhoneApplication} from "../../types/IPhoneApplication";
import {ApplicationSource, ApplicationSourceSection, getApplicationSourceDisplay} from "../../utils/ApplicationSource";
import {getStoreHash} from "../../utils/get_store_hash";
import {GetPhoneApplicationMessage} from "../../utils/text_generator/get_phone_application_message";

const validationSchema = Yup.object({
    phone: Yup.string().required()
});

interface IFormValues {
    phone: string;
}

interface IProps {
    source: ApplicationSource;
    vendorLogo?: string;
    offerId?: number;
    propertyId?: number;
    vendorId: number;
    region: IGtmRegion;
}

export const PhoneModal = (props: IProps) => {
    const {vendorId, offerId, propertyId, source} = props;
    const {isMobile} = useUserDevice();

    const [isOpen, setIsOpen] = useState(false);
    const [wasSeen, setWasSeen] = useState(false);
    const [showForm, setShowForm] = useState(true);

    const sourceSection = ApplicationSourceSection.MODAL;

    if (!offerId) {
        return null;
    }

    const storeHash = getStoreHash({vendorId, offerId, propertyId: propertyId || null});
    const applicationData = useSelector((state: IRPStore) => state.application[storeHash]);
    const leadData = useSelector((store: IRPStore) => store.application[storeHash]);
    const leadDataRequest = useSelector((store: IRPStore) => store.application[storeHash]?.leadDataRequest);
    const viewType = useSelector((state: IRPStore) => state.viewType.current);

    const formProps = useForm<IFormValues>({defaultValues: {phone: undefined}, resolver: yupResolver(validationSchema)});
    const dispatch = useDispatch();
    const {
        register: registerFormField,
        formState: {errors},
        setError,
        handleSubmit
    } = formProps;
    const [post, data] = usePostPhoneApplicationMutation();
    const sending = data.isLoading;

    useEffect(() => {
        if (!leadData) {
            dispatch(initializeApplicationRelatedDataAction(storeHash));
        }
    }, [leadData]);

    useEffect(() => {
        if (leadData && leadDataRequest === RequestState.None) {
            dispatch(fetchApplicationRelatedData(storeHash, vendorId, offerId, propertyId));
        }
    }, [leadData, leadDataRequest]);

    useEffect(() => {
        if (isOpen) {
            hitPhoneModalShow({source: props.source});
        }
    }, [isOpen]);

    useScrollObserver(
        ({scrollY}) => {
            const viewHeight = document?.body?.clientHeight || 0;
            const phoneModalTrigger = viewHeight * 0.3;

            if (scrollY >= phoneModalTrigger && !wasSeen) {
                setIsOpen(true);
            }
        },
        [],
        !wasSeen
    );

    useEffect(() => {
        if (data.error) {
            const errors = data.error as {[key: string]: string | string[] | {error: string}};
            const customError = Object.values(errors).find((value) => typeof value === "object" && !Array.isArray(value) && value.error) as {error: string};
            const isArchived = customError?.error.match(/Zgłoszenie nie może być wysłane na [\wśćę]+ archiwalną/gm);

            if (errors["phone"]) {
                setError("phone", {type: "custom", message: errors["phone"].toString()});
                return;
            }

            dispatch(
                isArchived
                    ? addWarningNotification("Przepraszamy, oferta na którą chcesz wysłać wiadomość została zarchiwizowana.", 7000)
                    : addFailureNotification("Coś poszło nie tak. Spróbuj ponownie później.")
            );
            isArchived && setTimeout(() => window.location.reload(), 6000);
        }

        if (data.isSuccess) {
            if (applicationData) {
                const {uuid, phone} = data.data;
                const {vendor, offer, property} = applicationData;
                const application: ITrackedApplication = {
                    email: "",
                    financing_services: false,
                    name: "",
                    phone,
                    source,
                    uuid
                };
                hitApplicationSuccess(application, sourceSection, {vendor, offer, property}, {extraPayloadViewType: viewType || undefined});
                gtmPhoneModalDetails({
                    region: props.region,
                    offer: offer?.detail ? {...offer?.detail, region: props.region.id} : undefined,
                    property: property?.detail ? property.detail : undefined,
                    sourceSection,
                    vendor: {id: vendor?.detail?.id as number, name: vendor?.detail?.name || ""},
                    viewType,
                    source,
                    uuid
                });
            }

            setShowForm(false);
        }
    }, [data]);

    const onSubmit = (formValues: IFormValues) => {
        const phone = formValues.phone.replace(/\s/g, "");

        const payload: IPhoneApplication = {
            source: props.source,
            source_section: ApplicationSourceSection.MODAL,
            ...(applicationData?.property?.detail?.id ? {property: applicationData.property.detail.id} : {offer: applicationData?.offer?.detail?.id}),
            phone,
            text: GetPhoneApplicationMessage({
                phone,
                offerName: applicationData?.offer?.detail?.name,
                propertyNumber: applicationData?.property?.detail?.number
            })
        };

        post(payload);
    };

    const onClose = () => {
        modalStatsHit({
            source,
            source_section: sourceSection,
            used: data.isSuccess,
            ...(propertyId ? {property_id: propertyId} : {}),
            offer_id: offerId,
            view_type: viewType as ViewType,
            vendor_id: vendorId,
            modal_version_name: getApplicationSourceDisplay(source)
        });
        setIsOpen(false);
        setWasSeen(true);
    };

    return (
        <RPModal
            isOpen={isOpen}
            onModalClose={onClose}
            closeButton={(onModalClose) => (
                <CloseIcon
                    size="2"
                    wrapperSize="4.8"
                    wrapperType="circle"
                    wrapperColor={isMobile ? "#fff" : "transparent"}
                    onClick={onModalClose}
                    css={closeButtonStyle}
                />
            )}
            css={phoneModalStyle}
        >
            {showForm ? (
                <Text variant="headline_4" as="p" align="center" mb="2.4rem" css={textStyle}>
                    Zostaw swój numer, a deweloper skontaktuje się z Tobą, aby przedstawić szczegóły oferty.{" "}
                </Text>
            ) : (
                <div css={thanksWrapper}>
                    <Image src={props.vendorLogo} width="77px" height="58px" alt="Logo dewelopera" />
                    <div>
                        <Text variant="headline_4" as="p" align="left">
                            Dziękujemy!
                        </Text>
                        <Text variant="body_copy_2">Oczekuj na kontakt od dewelopera</Text>
                    </div>
                </div>
            )}
            {showForm ? (
                <FormProvider {...formProps}>
                    <form onSubmit={handleSubmit(onSubmit)} css={width100}>
                        <InputField
                            placeholder="Wpisz numer telefonu"
                            css={inputStyle}
                            error={errors?.phone?.message}
                            {...transformReactHookFormProps(registerFormField("phone"))}
                        />

                        <Button variant="filled_primary" type="submit" size="x-small" css={sendButtonStyle}>
                            {sending ? <Loader size="sm" /> : "Zapisz"}
                        </Button>
                    </form>
                </FormProvider>
            ) : (
                <>
                    <Button
                        variant="filled_primary"
                        type="button"
                        size="x-small"
                        iconLeft={() => <ArrowIcon size="2" css={buttonIconMargin} />}
                        onClick={onClose}
                        css={width100}
                        disabled={sending}
                    >
                        Powróć
                    </Button>
                </>
            )}

            {showForm && (
                <>
                    <Text variant="info_txt_2" align="center">
                        Administratorem danych jest Property Group sp. z o.o. i deweloper, którego dotyczy zapytanie,{" "}
                        <Link href={rpAppLink.rodoTerms.base()} size="inherit" underline target="_blank">
                            zobacz, po co nam Twoje dane i jakie masz prawa.
                        </Link>
                    </Text>
                    <Text variant="info_txt_2" align="center">
                        Akceptuję{" "}
                        <Link href={rpAppLink.termsOfService.base()} size="inherit" underline target="_blank">
                            regulamin serwisu
                        </Link>
                    </Text>
                </>
            )}
        </RPModal>
    );
};

const buttonIconMargin = css`
    ${mr(1)};
`;

const width100 = css`
    width: 100%;
`;

const phoneModalStyle = css`
    bottom: -${calculateRemSize(1)};
    ${p(4)};
    border-radius: ${calculateRemSize(2)};
    overflow: unset;
    ${width100};

    @media (min-width: ${getThemeBreakpoint().md}) {
        bottom: unset;
        max-width: 460px;
    }
`;

const textStyle = css`
    align-self: center;
`;

const inputStyle = css`
    ${mb(3)};
`;

const thanksWrapper = css`
    display: flex;
    ${mb(3)};
    gap: ${calculateRemSize(2)};
`;

const sendButtonStyle = css`
    ${mb(3)};
    ${width100};
`;

const closeButtonStyle = css`
    cursor: pointer;
    position: absolute;
    top: -${calculateRemSize(7)};
    right: 50%;
    transform: translate(50%, 0);
    ${elevation()};

    @media (min-width: ${getThemeBreakpoint().md}) {
        top: -${calculateRemSize(3)};
        right: -${calculateRemSize(3)};
        transform: unset;
        ${elevation(0)};
    }
`;
import React, {useEffect, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {useDispatch, useSelector} from "react-redux";
import {css} from "@emotion/react";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {calculateRemSize, elevation, mb, mr, p} from "@pg-design/helpers-css-strings";
import {ArrowIcon, CloseIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {InputField} from "@pg-design/inputs-module";
import {Link} from "@pg-design/link-module";
import {Loader} from "@pg-design/loader-module";
import {transformReactHookFormProps} from "@pg-design/react-hook-forms-utils";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {useScrollObserver} from "@pg-mono/hooks";
import {RequestState} from "@pg-mono/request-state";
import {rpAppLink} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../app/rp_reducer";
import {RPModal} from "../../../atoms/RPModal";
import {addFailureNotification} from "../../../notifications/utils/add_failure_notification";
import {addWarningNotification} from "../../../notifications/utils/add_warning_notification";
import {hitApplicationSuccess, ITrackedApplication} from "../../../tracking/algolytics_hits/application_hit";
import {modalStatsHit} from "../../../tracking/algolytics_hits/modal_stats_hit";
import {IGtmRegion} from "../../../tracking/google_tag_manager/calculate_gtm_dimensions";
import {ViewType} from "../../../view_type/ViewType";
import {fetchApplicationRelatedData, initializeApplicationRelatedDataAction} from "../../actions/fetch_application_related_data";
import {usePostPhoneApplicationMutation} from "../../api/post_phone_application";
import {gtmPhoneModalDetails, hitPhoneModalShow} from "../../tracking/phone_modal_gtm";
import {IPhoneApplication} from "../../types/IPhoneApplication";
import {ApplicationSource, ApplicationSourceSection, getApplicationSourceDisplay} from "../../utils/ApplicationSource";
import {getStoreHash} from "../../utils/get_store_hash";
import {GetPhoneApplicationMessage} from "../../utils/text_generator/get_phone_application_message";

const validationSchema = Yup.object({
    phone: Yup.string().required()
});

interface IFormValues {
    phone: string;
}

interface IProps {
    source: ApplicationSource;
    vendorLogo?: string;
    offerId?: number;
    propertyId?: number;
    vendorId: number;
    region: IGtmRegion;
}

export const PhoneModal = (props: IProps) => {
    const {vendorId, offerId, propertyId, source} = props;
    const {isMobile} = useUserDevice();

    const [isOpen, setIsOpen] = useState(false);
    const [wasSeen, setWasSeen] = useState(false);
    const [showForm, setShowForm] = useState(true);

    const sourceSection = ApplicationSourceSection.MODAL;

    if (!offerId) {
        return null;
    }

    const storeHash = getStoreHash({vendorId, offerId, propertyId: propertyId || null});
    const applicationData = useSelector((state: IRPStore) => state.application[storeHash]);
    const leadData = useSelector((store: IRPStore) => store.application[storeHash]);
    const leadDataRequest = useSelector((store: IRPStore) => store.application[storeHash]?.leadDataRequest);
    const viewType = useSelector((state: IRPStore) => state.viewType.current);

    const formProps = useForm<IFormValues>({defaultValues: {phone: undefined}, resolver: yupResolver(validationSchema)});
    const dispatch = useDispatch();
    const {
        register: registerFormField,
        formState: {errors},
        setError,
        handleSubmit
    } = formProps;
    const [post, data] = usePostPhoneApplicationMutation();
    const sending = data.isLoading;

    useEffect(() => {
        if (!leadData) {
            dispatch(initializeApplicationRelatedDataAction(storeHash));
        }
    }, [leadData]);

    useEffect(() => {
        if (leadData && leadDataRequest === RequestState.None) {
            dispatch(fetchApplicationRelatedData(storeHash, vendorId, offerId, propertyId));
        }
    }, [leadData, leadDataRequest]);

    useEffect(() => {
        if (isOpen) {
            hitPhoneModalShow({source: props.source});
        }
    }, [isOpen]);

    useScrollObserver(
        ({scrollY}) => {
            const viewHeight = document?.body?.clientHeight || 0;
            const phoneModalTrigger = viewHeight * 0.3;

            if (scrollY >= phoneModalTrigger && !wasSeen) {
                setIsOpen(true);
            }
        },
        [],
        !wasSeen
    );

    useEffect(() => {
        if (data.error) {
            const errors = data.error as {[key: string]: string | string[] | {error: string}};
            const customError = Object.values(errors).find((value) => typeof value === "object" && !Array.isArray(value) && value.error) as {error: string};
            const isArchived = customError?.error.match(/Zgłoszenie nie może być wysłane na [\wśćę]+ archiwalną/gm);

            if (errors["phone"]) {
                setError("phone", {type: "custom", message: errors["phone"].toString()});
                return;
            }

            dispatch(
                isArchived
                    ? addWarningNotification("Przepraszamy, oferta na którą chcesz wysłać wiadomość została zarchiwizowana.", 7000)
                    : addFailureNotification("Coś poszło nie tak. Spróbuj ponownie później.")
            );
            isArchived && setTimeout(() => window.location.reload(), 6000);
        }

        if (data.isSuccess) {
            if (applicationData) {
                const {uuid, phone} = data.data;
                const {vendor, offer, property} = applicationData;
                const application: ITrackedApplication = {
                    email: "",
                    financing_services: false,
                    name: "",
                    phone,
                    source,
                    uuid
                };
                hitApplicationSuccess(application, sourceSection, {vendor, offer, property}, {extraPayloadViewType: viewType || undefined});
                gtmPhoneModalDetails({
                    region: props.region,
                    offer: offer?.detail ? {...offer?.detail, region: props.region.id} : undefined,
                    property: property?.detail ? property.detail : undefined,
                    sourceSection,
                    vendor: {id: vendor?.detail?.id as number, name: vendor?.detail?.name || ""},
                    viewType,
                    source,
                    uuid
                });
            }

            setShowForm(false);
        }
    }, [data]);

    const onSubmit = (formValues: IFormValues) => {
        const phone = formValues.phone.replace(/\s/g, "");

        const payload: IPhoneApplication = {
            source: props.source,
            source_section: ApplicationSourceSection.MODAL,
            ...(applicationData?.property?.detail?.id ? {property: applicationData.property.detail.id} : {offer: applicationData?.offer?.detail?.id}),
            phone,
            text: GetPhoneApplicationMessage({
                phone,
                offerName: applicationData?.offer?.detail?.name,
                propertyNumber: applicationData?.property?.detail?.number
            })
        };

        post(payload);
    };

    const onClose = () => {
        modalStatsHit({
            source,
            source_section: sourceSection,
            used: data.isSuccess,
            ...(propertyId ? {property_id: propertyId} : {}),
            offer_id: offerId,
            view_type: viewType as ViewType,
            vendor_id: vendorId,
            modal_version_name: getApplicationSourceDisplay(source)
        });
        setIsOpen(false);
        setWasSeen(true);
    };

    return (
        <RPModal
            isOpen={isOpen}
            onModalClose={onClose}
            closeButton={(onModalClose) => (
                <CloseIcon
                    size="2"
                    wrapperSize="4.8"
                    wrapperType="circle"
                    wrapperColor={isMobile ? "#fff" : "transparent"}
                    onClick={onModalClose}
                    css={closeButtonStyle}
                />
            )}
            css={phoneModalStyle}
        >
            {showForm ? (
                <Text variant="headline_4" as="p" align="center" mb="2.4rem" css={textStyle}>
                    Zostaw swój numer, a deweloper skontaktuje się z Tobą, aby przedstawić szczegóły oferty.{" "}
                </Text>
            ) : (
                <div css={thanksWrapper}>
                    <Image src={props.vendorLogo} width="77px" height="58px" alt="Logo dewelopera" />
                    <div>
                        <Text variant="headline_4" as="p" align="left">
                            Dziękujemy!
                        </Text>
                        <Text variant="body_copy_2">Oczekuj na kontakt od dewelopera</Text>
                    </div>
                </div>
            )}
            {showForm ? (
                <FormProvider {...formProps}>
                    <form onSubmit={handleSubmit(onSubmit)} css={width100}>
                        <InputField
                            placeholder="Wpisz numer telefonu"
                            css={inputStyle}
                            error={errors?.phone?.message}
                            {...transformReactHookFormProps(registerFormField("phone"))}
                        />

                        <Button variant="filled_primary" type="submit" size="x-small" css={sendButtonStyle}>
                            {sending ? <Loader size="sm" /> : "Zapisz"}
                        </Button>
                    </form>
                </FormProvider>
            ) : (
                <>
                    <Button
                        variant="filled_primary"
                        type="button"
                        size="x-small"
                        iconLeft={() => <ArrowIcon size="2" css={buttonIconMargin} />}
                        onClick={onClose}
                        css={width100}
                        disabled={sending}
                    >
                        Powróć
                    </Button>
                </>
            )}

            {showForm && (
                <>
                    <Text variant="info_txt_2" align="center">
                        Administratorem danych jest Property Group sp. z o.o. i deweloper, którego dotyczy zapytanie,{" "}
                        <Link href={rpAppLink.rodoTerms.base()} size="inherit" underline target="_blank">
                            zobacz, po co nam Twoje dane i jakie masz prawa.
                        </Link>
                    </Text>
                    <Text variant="info_txt_2" align="center">
                        Akceptuję{" "}
                        <Link href={rpAppLink.termsOfService.base()} size="inherit" underline target="_blank">
                            regulamin serwisu
                        </Link>
                    </Text>
                </>
            )}
        </RPModal>
    );
};

const buttonIconMargin = css`
    ${mr(1)};
`;

const width100 = css`
    width: 100%;
`;

const phoneModalStyle = css`
    bottom: -${calculateRemSize(1)};
    ${p(4)};
    border-radius: ${calculateRemSize(2)};
    overflow: unset;
    ${width100};

    @media (min-width: ${getThemeBreakpoint().md}) {
        bottom: unset;
        max-width: 460px;
    }
`;

const textStyle = css`
    align-self: center;
`;

const inputStyle = css`
    ${mb(3)};
`;

const thanksWrapper = css`
    display: flex;
    ${mb(3)};
    gap: ${calculateRemSize(2)};
`;

const sendButtonStyle = css`
    ${mb(3)};
    ${width100};
`;

const closeButtonStyle = css`
    cursor: pointer;
    position: absolute;
    top: -${calculateRemSize(7)};
    right: 50%;
    transform: translate(50%, 0);
    ${elevation()};

    @media (min-width: ${getThemeBreakpoint().md}) {
        top: -${calculateRemSize(3)};
        right: -${calculateRemSize(3)};
        transform: unset;
        ${elevation(0)};
    }
`;
